/**
 * Pure helpers for the Acuity table's dynamic year columns (OR-1328):
 * year validation, manual column order persistence (localStorage — the
 * backend has no user-preferences endpoint yet, same gap as OR-1384),
 * derived "All" totals, the Beds Sum footer and the Missing Data
 * computation. Kept page-agnostic so the Survey tab redesign can reuse
 * them when its ticket lands.
 */
import { ACUITY_CARE_TYPES } from "../../../api/adapters/regulatory";
import type { AcuityRow } from "./types";

/** Backend occupancy constraint: `year` must be 2000–2100. */
export function isValidYearLabel(value: string): boolean {
    if (!/^\d{4}$/.test(value)) return false;
    const n = Number(value);
    return n >= 2000 && n <= 2100;
}

// ─── Column order persistence ───────────────────────────────────────────────

function yearOrderStorageKey(submissionId: string): string {
    return `orion.regulatory.acuity.yearOrder.${submissionId}`;
}

/** Manual column order for this submission, or [] when none stored /
 *  storage unavailable. */
export function readStoredYearOrder(submissionId: string): string[] {
    try {
        const raw = window.localStorage.getItem(yearOrderStorageKey(submissionId));
        const parsed: unknown = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) ? parsed.map(String).filter((y) => /^\d{4}$/.test(y)) : [];
    } catch {
        return [];
    }
}

export function storeYearOrder(submissionId: string, years: readonly string[]): void {
    try {
        window.localStorage.setItem(yearOrderStorageKey(submissionId), JSON.stringify(years));
    } catch {
        // Storage full / disabled — the order just won't survive a reload.
    }
}

/** Overlay a stored manual order onto the data-derived year list: stored
 *  years keep their relative order (dropping ones the data no longer has),
 *  and data years the store doesn't know yet append after, preserving the
 *  incoming (newest-first) order. */
export function applyStoredYearOrder(years: readonly string[], stored: readonly string[]): string[] {
    const present = new Set(years);
    const ordered = stored.filter((y) => present.has(y));
    const known = new Set(ordered);
    return [...ordered, ...years.filter((y) => !known.has(y))];
}

// ─── Derived values ─────────────────────────────────────────────────────────

function toIntOrNull(v: string): number | null {
    const t = v.trim();
    if (t === "") return null;
    const n = Number(t.replace(/,/g, ""));
    return Number.isFinite(n) ? Math.trunc(n) : null;
}

/** The facility's "All" value for a year — the sum of the four care-type
 *  census cells. Returns null when EVERY cell is empty (missing data →
 *  the table renders a dash); a confirmed "0" cell still yields a sum. */
export function facilityYearTotal(row: AcuityRow, year: string): number | null {
    let total = 0;
    let hasValue = false;
    for (const { field } of ACUITY_CARE_TYPES) {
        const n = toIntOrNull(row.census[field]?.[year] ?? "");
        if (n !== null) {
            total += n;
            hasValue = true;
        }
    }
    return hasValue ? total : null;
}

/** Beds Sum footer value for a year — the sum of every facility's "All"
 *  plus any draft-row values for that year. Null when nothing numeric
 *  contributes (e.g. a just-added empty column → dash). */
export function bedsSumForYear(
    rows: readonly AcuityRow[],
    draftValues: readonly Record<string, string>[],
    year: string,
): number | null {
    let total = 0;
    let hasValue = false;
    for (const row of rows) {
        const t = facilityYearTotal(row, year);
        if (t !== null) {
            total += t;
            hasValue = true;
        }
    }
    for (const values of draftValues) {
        const n = toIntOrNull(values[year] ?? "");
        if (n !== null) {
            total += n;
            hasValue = true;
        }
    }
    return hasValue ? total : null;
}

// ─── Missing data (alert) ───────────────────────────────────────────────────

/** True when a facility's year has a gap: the year has NO census value at all
 *  (every care-type cell empty → the "All" total renders a dash). A partially
 *  filled year is NOT a gap — we don't force the user to enter zeros in every
 *  care type (OR-1417, Cavero jul-23). A confirmed "0" counts as data. */
export function facilityYearHasGap(row: AcuityRow, year: string): boolean {
    return facilityYearTotal(row, year) === null;
}

/** Per-facility list of rendered years that have a gap (any care-type cell
 *  missing), newest-year-first following the rendered column order.
 *  Facilities with no gaps are omitted. */
export function missingYearsByFacility(
    rows: readonly AcuityRow[],
    years: readonly string[],
): Map<string, string[]> {
    const missing = new Map<string, string[]>();
    for (const row of rows) {
        const gaps = years.filter((y) => facilityYearHasGap(row, y));
        if (gaps.length > 0) missing.set(row.id, gaps);
    }
    return missing;
}
